import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { bugReports } from '../../services/api';
import { BugReport, BugReportMetadata, BugReportNote } from 'types';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { useToast } from '../../components/ui/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import {
  normalizeSearch,
  buildOptionLabelMap,
} from './FeedbackReports/utils';
import type { FeedbackReportFilters, DuplicateOption } from './FeedbackReports/types';
import { FeedbackReportsFilters } from './FeedbackReports/FeedbackReportsFilters';
import { FeedbackReportsTable } from './FeedbackReports/FeedbackReportsTable';
import { FeedbackReportDetail } from './FeedbackReports/FeedbackReportDetail';

const PAGE_SIZE = 20;
const DUPLICATE_PAGE_SIZE = 8;
const CLOSED_STATUS = 'closed';
const SEVERITY_STATUSES = new Set(['ready_to_start', 'in_progress', 'blocked', 'resolved', 'deferred']);
const TRIAGE_NEEDED_STATUS = 'triage_needed';

const FeedbackReports: React.FC = () => {
  const { addToast } = useToast();
  const { user: currentUser } = useAuth();

  const [metadata, setMetadata] = useState<BugReportMetadata>({
    status_choices: [],
    closed_reason_choices: [],
    severity_choices: [],
  });
  const [reports, setReports] = useState<BugReport[]>([]);

  const fetchReports = useCallback(
    async ({
      page: nextPage,
      pageSize: nextPageSize,
      search: nextSearch,
      filters: nextFilters,
    }: {
      page: number;
      pageSize: number;
      search?: string;
      filters: FeedbackReportFilters;
    }) => {
      const params: Record<string, string | number | string[]> = {
        page: nextPage,
        page_size: nextPageSize,
      };

      if (nextSearch) {
        params.search = nextSearch;
      }

      if (nextFilters.status.length > 0) {
        params.status = nextFilters.status;
      }

      if (nextFilters.severity.length > 0) {
        params.severity = nextFilters.severity;
      }

      const data = await bugReports.list(params);

      if (Array.isArray(data)) {
        return { data: data as BugReport[], total: data.length };
      }

      const results = Array.isArray((data as { results?: BugReport[] })?.results)
        ? ((data as { results: BugReport[] }).results)
        : [];
      const total =
        typeof (data as { count?: number })?.count === 'number'
          ? (data as { count: number }).count
          : results.length;

      return { data: results, total };
    },
    [],
  );

  const {
    data: pagedReports,
    total,
    loading: listLoading,
    error: listError,
    page,
    pageSize,
    search,
    setSearch,
    appliedSearch,
    filters,
    setFilters,
    setPage,
    refetch,
  } = usePaginatedData<BugReport, FeedbackReportFilters>({
    fetcher: fetchReports,
    initialPageSize: PAGE_SIZE,
    initialFilters: { status: [TRIAGE_NEEDED_STATUS], severity: [] },
  });

  const statusFilters = filters.status ?? [];
  const severityFilters = filters.severity ?? [];

  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formStatus, setFormStatus] = useState<string>('');
  const [formClosedReason, setFormClosedReason] = useState<string | null>(null);
  const [formDuplicate, setFormDuplicate] = useState<DuplicateOption | null>(null);
  const [formSeverity, setFormSeverity] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  const [notes, setNotes] = useState<BugReportNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');
  const [editingNoteSaving, setEditingNoteSaving] = useState(false);
  const [editingNoteError, setEditingNoteError] = useState<string | null>(null);
  const [editingFeedback, setEditingFeedback] = useState(false);
  const [feedbackDraft, setFeedbackDraft] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFeedbackText, setCreateFeedbackText] = useState('');
  const [createStatus, setCreateStatus] = useState<string>(TRIAGE_NEEDED_STATUS);
  const [createSeverity, setCreateSeverity] = useState<string>('');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const requiresSeverity = useMemo(() => SEVERITY_STATUSES.has(formStatus), [formStatus]);
  const requiresClosedReason = useMemo(() => formStatus === CLOSED_STATUS, [formStatus]);
  const isDuplicateRequired = formClosedReason === 'duplicate';
  const isSaveDisabled =
    saving ||
    !formStatus ||
    (requiresClosedReason && !formClosedReason) ||
    (isDuplicateRequired && !formDuplicate) ||
    (requiresSeverity && !formSeverity);
  const createRequiresSeverity = useMemo(
    () => SEVERITY_STATUSES.has(createStatus),
    [createStatus],
  );
  const isCreateDisabled =
    createSaving ||
    normalizeSearch(createFeedbackText).length === 0 ||
    (createRequiresSeverity && !createSeverity);
  const isFeedbackSaveDisabled =
    feedbackSaving || normalizeSearch(feedbackDraft).length === 0;

  const [duplicatePickerOpen, setDuplicatePickerOpen] = useState(false);
  const [duplicateSearch, setDuplicateSearch] = useState('');
  const [duplicateAppliedSearch, setDuplicateAppliedSearch] = useState('');
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState<BugReport[]>([]);

  useEffect(() => {
    let cancelled = false;
    bugReports
      .metadata()
      .then((data) => {
        if (cancelled) {
          return;
        }
        const normalized: BugReportMetadata = {
          status_choices: Array.isArray(data?.status_choices) ? data.status_choices : [],
          closed_reason_choices: Array.isArray(data?.closed_reason_choices)
            ? data.closed_reason_choices
            : [],
          severity_choices: Array.isArray(data?.severity_choices)
            ? data.severity_choices
            : [],
        };
        setMetadata(normalized);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load bug report metadata', error);
        addToast({
          title: 'Unable to load metadata',
          description: 'Status and closed reason options may be incomplete.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const handleResetForm = useCallback(() => {
    if (!selectedReport) {
      return;
    }
    setFormStatus(selectedReport.status);
    setFormClosedReason(selectedReport.closed_reason ?? null);
    setFormDuplicate(selectedReport.duplicate_of_summary ?? null);
    setFormSeverity(
      SEVERITY_STATUSES.has(selectedReport.status) ? selectedReport.severity ?? '' : '',
    );
    setFormError(null);
    setEditingFeedback(false);
    setFeedbackDraft(selectedReport.feedback_text ?? '');
    setFeedbackError(null);
  }, [selectedReport]);

  const refreshList = useCallback(() => {
    void refetch({ force: true });
  }, [refetch]);

  useEffect(() => {
    setReports(pagedReports);
  }, [pagedReports]);

  useEffect(() => {
    setSelectedReportId((prev) => {
      if (reports.length === 0) {
        return null;
      }
      if (prev && reports.some((item) => item.id === prev)) {
        return prev;
      }
      return reports[0].id;
    });
  }, [reports]);

  useEffect(() => {
    if (metadata.status_choices.length === 0) {
      const sample = reports.find(
        (item) => Array.isArray(item.status_choices) && item.status_choices.length > 0,
      );
      if (sample) {
        setMetadata((prev) => ({
          status_choices: sample.status_choices || prev.status_choices,
          closed_reason_choices: sample.closed_reason_choices || prev.closed_reason_choices,
          severity_choices: sample.severity_choices || prev.severity_choices,
        }));
      }
    }
  }, [reports, metadata.status_choices, metadata.closed_reason_choices, metadata.severity_choices]);

  useEffect(() => {
    if (listError) {
      addToast({
        title: 'Unable to load bug reports',
        description: listError,
      });
    }
  }, [addToast, listError]);

  const loadNotes = useCallback(
    async (bugReportId: number) => {
      setNotesLoading(true);
      try {
        const data = await bugReports.listNotes(bugReportId, 100);
        const results = Array.isArray(data)
          ? data
          : Array.isArray((data as { results?: BugReportNote[] })?.results)
            ? (data as { results: BugReportNote[] }).results
            : [];
        setNotes(results);
      } catch (error) {
        console.error('Failed to load bug report notes', error);
        addToast({
          title: 'Unable to load notes',
          description: 'Refreshing notes failed. Please try again.',
        });
      } finally {
        setNotesLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (!selectedReportId) {
      setSelectedReport(null);
      setFormStatus('');
      setFormClosedReason(null);
      setFormDuplicate(null);
      setFormSeverity('');
      setNotes([]);
      setEditingFeedback(false);
      setFeedbackDraft('');
      setFeedbackSaving(false);
      setFeedbackError(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);

    bugReports
      .retrieve(selectedReportId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setSelectedReport(data);
        setFormStatus(data.status);
        setFormClosedReason(data.closed_reason ?? null);
        setFormDuplicate(data.duplicate_of_summary ?? null);
        setFormSeverity(SEVERITY_STATUSES.has(data.status) ? data.severity ?? '' : '');
        setFormError(null);
        setEditingFeedback(false);
        setFeedbackDraft(data.feedback_text ?? '');
        setFeedbackSaving(false);
        setFeedbackError(null);

        if (Array.isArray(data.notes)) {
          setNotes(data.notes);
        } else {
          setNotes([]);
        }

        setMetadata((prev) => {
          const next: BugReportMetadata = {
            status_choices: prev.status_choices,
            closed_reason_choices: prev.closed_reason_choices,
            severity_choices: prev.severity_choices,
          };
          let changed = false;

          const incomingStatusChoices = Array.isArray(data.status_choices) ? data.status_choices : [];
          if (incomingStatusChoices.length > 0 && prev.status_choices.length === 0) {
            next.status_choices = incomingStatusChoices;
            changed = true;
          }

          const incomingClosedReasons = Array.isArray(data.closed_reason_choices)
            ? data.closed_reason_choices
            : [];
          if (incomingClosedReasons.length > 0 && prev.closed_reason_choices.length === 0) {
            next.closed_reason_choices = incomingClosedReasons;
            changed = true;
          }

          const incomingSeverityChoices = Array.isArray(data.severity_choices)
            ? data.severity_choices
            : [];
          if (incomingSeverityChoices.length > 0 && prev.severity_choices.length === 0) {
            next.severity_choices = incomingSeverityChoices;
            changed = true;
          }

          return changed ? next : prev;
        });

        loadNotes(data.id);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load bug report detail', error);
        addToast({
          title: 'Unable to load report',
          description: 'Please select a different report or try again.',
        });
        setSelectedReport(null);
        setEditingFeedback(false);
        setFeedbackDraft('');
        setFeedbackSaving(false);
        setFeedbackError(null);
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedReportId, loadNotes, addToast]);

  useEffect(() => {
    if (!requiresSeverity && formSeverity) {
      setFormSeverity('');
    }
  }, [requiresSeverity, formSeverity]);

  useEffect(() => {
    if (!requiresClosedReason) {
      if (formClosedReason) {
        setFormClosedReason(null);
      }
      if (formDuplicate) {
        setFormDuplicate(null);
      }
    }
  }, [requiresClosedReason, formClosedReason, formDuplicate]);

  useEffect(() => {
    if (!duplicatePickerOpen) {
      return;
    }
    const handler = window.setTimeout(() => {
      setDuplicateAppliedSearch(normalizeSearch(duplicateSearch));
    }, 250);
    return () => window.clearTimeout(handler);
  }, [duplicatePickerOpen, duplicateSearch]);

  useEffect(() => {
    if (!duplicatePickerOpen || !selectedReportId) {
      return;
    }

    let cancelled = false;
    setDuplicateLoading(true);

    bugReports
      .list({
        search: duplicateAppliedSearch || undefined,
        page: 1,
        page_size: DUPLICATE_PAGE_SIZE,
        exclude: selectedReportId,
      })
      .then((data) => {
        if (cancelled) {
          return;
        }
        const results = Array.isArray(data)
          ? data
          : Array.isArray((data as { results?: BugReport[] })?.results)
            ? ((data as { results: BugReport[] }).results)
            : [];
        setDuplicateResults(results);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to search duplicates', error);
        addToast({
          title: 'Duplicate search failed',
          description: 'Try adjusting your search terms.',
        });
        setDuplicateResults([]);
      })
      .finally(() => {
        if (!cancelled) {
          setDuplicateLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [duplicatePickerOpen, duplicateAppliedSearch, selectedReportId, addToast]);

  const statusLabelMap = useMemo(
    () => buildOptionLabelMap(metadata.status_choices),
    [metadata.status_choices],
  );

  const severityLabelMap = useMemo(
    () => buildOptionLabelMap(metadata.severity_choices),
    [metadata.severity_choices],
  );

  const selectedReportMetadataEntries = useMemo(() => {
    const meta = selectedReport?.metadata;
    if (!meta || typeof meta !== 'object') {
      return [];
    }
    return Object.entries(meta as Record<string, unknown>);
  }, [selectedReport]);

  const resetCreateForm = () => {
    setCreateFeedbackText('');
    setCreateStatus(TRIAGE_NEEDED_STATUS);
    setCreateSeverity('');
    setCreateError(null);
  };

  const handleStartEditingFeedback = () => {
    if (!selectedReport) {
      return;
    }
    setFeedbackDraft(selectedReport.feedback_text ?? '');
    setFeedbackError(null);
    setEditingFeedback(true);
  };

  const handleFeedbackDraftChange = (value: string) => {
    setFeedbackDraft(value);
    if (feedbackError) {
      setFeedbackError(null);
    }
  };

  const handleCancelEditingFeedback = () => {
    if (feedbackSaving) {
      return;
    }
    setEditingFeedback(false);
    setFeedbackDraft(selectedReport?.feedback_text ?? '');
    setFeedbackError(null);
  };

  const handleSaveFeedback = async () => {
    if (!selectedReportId || !selectedReport || feedbackSaving) {
      return;
    }
    const trimmedFeedback = feedbackDraft.trim();
    if (trimmedFeedback.length === 0) {
      setFeedbackError('Feedback cannot be empty.');
      return;
    }
    setFeedbackSaving(true);
    setFeedbackError(null);
    try {
      const updated = await bugReports.update(selectedReportId, {
        feedback_text: trimmedFeedback,
      });
      setSelectedReport(updated);
      setFeedbackDraft(updated.feedback_text ?? trimmedFeedback);
      setEditingFeedback(false);
      setFormStatus(updated.status);
      setFormClosedReason(updated.closed_reason ?? null);
      setFormDuplicate(updated.duplicate_of_summary ?? null);
      setFormSeverity(
        SEVERITY_STATUSES.has(updated.status) ? updated.severity ?? '' : '',
      );
      setReports((prev) =>
        prev.map((report) =>
          report.id === updated.id ? { ...report, feedback_text: updated.feedback_text } : report,
        ),
      );
      refreshList();
      addToast({
        title: 'Feedback updated',
        description: 'Report feedback has been updated.',
      });
    } catch (error) {
      console.error('Failed to update bug report feedback', error);
      setFeedbackError('We could not update the feedback. Please try again.');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const handleCreateDialogToggle = (open: boolean) => {
    if (!open && createSaving) {
      return;
    }
    if (!open) {
      resetCreateForm();
    } else {
      setCreateError(null);
    }
    setCreateDialogOpen(open);
  };

  const handleCreateFeedbackChange = (value: string) => {
    setCreateFeedbackText(value);
    if (createError) {
      setCreateError(null);
    }
  };

  const handleCreateStatusChange = (value: string) => {
    setCreateStatus(value);
    if (SEVERITY_STATUSES.has(value)) {
      setCreateSeverity((prev) => prev || metadata.severity_choices[0]?.value || '');
    } else {
      setCreateSeverity('');
    }
    setCreateError(null);
  };

  const handleCreateSeverityChange = (value: string) => {
    setCreateSeverity(value);
    setCreateError(null);
  };

  const handleFormStatusChange = (value: string) => {
    setFormStatus(value);
    setFormError(null);
  };

  const handleFormClosedReasonChange = (value: string | null) => {
    setFormClosedReason(value);
    setFormError(null);
  };

  const handleFormSeverityChange = (value: string) => {
    setFormSeverity(value);
    setFormError(null);
  };

  const handleCreateReport = async () => {
    if (createSaving) {
      return;
    }
    const normalizedFeedback = normalizeSearch(createFeedbackText);
    if (normalizedFeedback.length === 0) {
      setCreateError('Enter feedback details before creating a report.');
      return;
    }
    if (createRequiresSeverity && !createSeverity) {
      setCreateError('Select a severity before creating the report.');
      return;
    }
    setCreateError(null);
    setCreateSaving(true);
    try {
      const newReport = await bugReports.create({
        feedback_text: createFeedbackText.trim(),
        status: createStatus || undefined,
        severity: createRequiresSeverity ? createSeverity : undefined,
      });
      addToast({
        title: 'Report created',
        description: `Report #${newReport.id} has been created.`,
      });
      resetCreateForm();
      setCreateDialogOpen(false);
      setSelectedReportId(newReport.id);
      setSelectedReport(newReport);
      setFormStatus(newReport.status);
      setFormClosedReason(newReport.closed_reason ?? null);
      setFormDuplicate(newReport.duplicate_of_summary ?? null);
      setFormSeverity(
        SEVERITY_STATUSES.has(newReport.status) ? newReport.severity ?? '' : '',
      );
      setFormError(null);
      setNotes(Array.isArray(newReport.notes) ? newReport.notes : []);
      setEditingFeedback(false);
      setFeedbackDraft(newReport.feedback_text ?? '');
      setFeedbackError(null);
      await loadNotes(newReport.id);
      refreshList();
    } catch (error) {
      console.error('Failed to create bug report', error);
      setCreateError('We could not create the report. Please try again.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleStartEditingNote = (note: BugReportNote) => {
    setEditingNoteId(note.id);
    setEditingNoteValue(note.note);
    setEditingNoteError(null);
  };

  const handleEditingNoteValueChange = (value: string) => {
    setEditingNoteValue(value);
    if (editingNoteError) {
      setEditingNoteError(null);
    }
  };

  const handleCancelEditingNote = () => {
    if (editingNoteSaving) {
      return;
    }
    setEditingNoteId(null);
    setEditingNoteValue('');
    setEditingNoteError(null);
  };

  const handleSaveEditedNote = async () => {
    if (!selectedReportId || editingNoteId === null || editingNoteSaving) {
      return;
    }
    const normalizedContent = normalizeSearch(editingNoteValue);
    if (normalizedContent.length === 0) {
      setEditingNoteError('Note cannot be empty.');
      return;
    }
    setEditingNoteSaving(true);
    setEditingNoteError(null);
    try {
      await bugReports.updateNote(editingNoteId, editingNoteValue);
      await loadNotes(selectedReportId);
      addToast({
        title: 'Note updated',
        description: 'Your note has been updated.',
      });
      setEditingNoteId(null);
      setEditingNoteValue('');
    } catch (error) {
      console.error('Failed to update bug report note', error);
      setEditingNoteError('We could not update the note. Please try again.');
    } finally {
      setEditingNoteSaving(false);
    }
  };


  const handleStatusToggle = (value: string) => {
    const nextStatus = statusFilters.includes(value)
      ? statusFilters.filter((item) => item !== value)
      : [...statusFilters, value];
    setFilters({ status: nextStatus, severity: severityFilters });
  };

  const handleSeverityToggle = (value: string) => {
    const nextSeverity = severityFilters.includes(value)
      ? severityFilters.filter((item) => item !== value)
      : [...severityFilters, value];
    setFilters({ status: statusFilters, severity: nextSeverity });
  };

  const handleClearFilters = () => {
    setFilters({ status: [], severity: [] });
    setSearch('');
    setPage(1);
  };

  const handleSave = async () => {
    if (!selectedReportId) {
      return;
    }
    if (!formStatus) {
      setFormError('Select a status before saving.');
      return;
    }
    if (requiresClosedReason && !formClosedReason) {
      setFormError('Select a closed reason before saving.');
      return;
    }
    if (formClosedReason === 'duplicate' && !formDuplicate) {
      setFormError('Select the canonical report for duplicates.');
      return;
    }
    const shouldIncludeSeverity = SEVERITY_STATUSES.has(formStatus);
    if (shouldIncludeSeverity && !formSeverity) {
      setFormError('Select a severity before saving.');
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      const effectiveSeverity = shouldIncludeSeverity
        ? formSeverity ||
          selectedReport?.severity ||
          metadata.severity_choices[0]?.value ||
          'medium'
        : '';

      const updated = await bugReports.update(selectedReportId, {
        status: formStatus,
        closed_reason: formClosedReason ?? null,
        severity: shouldIncludeSeverity ? effectiveSeverity : undefined,
        duplicate_of_id: formClosedReason === 'duplicate' ? formDuplicate?.id ?? null : null,
      });
      setSelectedReport(updated);
      setFormStatus(updated.status);
      setFormClosedReason(updated.closed_reason ?? null);
      setFormDuplicate(updated.duplicate_of_summary ?? null);
      setFormSeverity(
        SEVERITY_STATUSES.has(updated.status) ? updated.severity ?? effectiveSeverity : ''
      );
      setEditingFeedback(false);
      setFeedbackDraft(updated.feedback_text ?? '');
      setFeedbackError(null);
      await loadNotes(updated.id);
      refreshList();
      addToast({
        title: 'Report updated',
        description: `Report #${updated.id} has been saved.`,
      });
    } catch (error) {
      console.error('Failed to update bug report', error);
      addToast({
        title: 'Save failed',
        description: 'We could not update the report. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedReportId) {
      return;
    }
    const content = normalizeSearch(noteDraft);
    if (content.length === 0) {
      return;
    }
    setNoteSaving(true);
    try {
      await bugReports.createNote(selectedReportId, noteDraft);
      setNoteDraft('');
      await loadNotes(selectedReportId);
      addToast({
        title: 'Note added',
        description: 'Your note has been attached to the report.',
      });
    } catch (error) {
      console.error('Failed to add note', error);
      addToast({
        title: 'Could not add note',
        description: 'Please try submitting the note again.',
      });
    } finally {
      setNoteSaving(false);
    }
  };

  const handleNoteDraftChange = (value: string) => {
    setNoteDraft(value);
  };

  const handleSelectDuplicate = (report: BugReport) => {
    setFormDuplicate({
      id: report.id,
      status: report.status,
      status_display: report.status_display,
      feedback_text: report.feedback_text,
      created_at: report.created_at,
    });
    handleDuplicatePickerToggle(false);
  };

  const handleDuplicatePickerToggle = (open: boolean) => {
    setDuplicatePickerOpen(open);
    if (!open) {
      setDuplicateSearch('');
      setDuplicateAppliedSearch('');
    }
  };

  const handleDuplicateSearchChange = (value: string) => {
    setDuplicateSearch(value);
  };

  return (
    <div className="flex flex-col gap-6">
      <Card maxWidth="none">
        <CardContent variant="custom" className="space-y-6">
          <CardTitle
            title="Feedback"
            subtitle="Review, triage, and resolve customer-submitted bug reports."
            subtitleSize="big"
          />
          <div className="grid gap-4 xl:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
            <FeedbackReportsFilters
              search={search}
              onSearchChange={setSearch}
              statusFilters={statusFilters}
              severityFilters={severityFilters}
              metadata={metadata}
              onToggleStatus={handleStatusToggle}
              onToggleSeverity={handleSeverityToggle}
              onClearFilters={handleClearFilters}
              appliedSearch={appliedSearch}
              createDialogOpen={createDialogOpen}
              onToggleCreateDialog={handleCreateDialogToggle}
              createFeedbackText={createFeedbackText}
              onChangeCreateFeedbackText={handleCreateFeedbackChange}
              createError={createError}
              createStatus={createStatus}
              onChangeCreateStatus={handleCreateStatusChange}
              createRequiresSeverity={createRequiresSeverity}
              createSeverity={createSeverity}
              onChangeCreateSeverity={handleCreateSeverityChange}
              createSaving={createSaving}
              isCreateDisabled={isCreateDisabled}
              onSubmitCreate={handleCreateReport}
            />
            <FeedbackReportsTable
              reports={reports}
              loading={listLoading}
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              selectedReportId={selectedReportId}
              onSelectReport={(id) => setSelectedReportId(id)}
              statusLabelMap={statusLabelMap}
              severityLabelMap={severityLabelMap}
            />
          </div>
        </CardContent>
      </Card>

            <FeedbackReportDetail
        detailLoading={detailLoading}
        selectedReport={selectedReport}
        selectedReportId={selectedReportId}
        currentUserId={currentUser?.id ?? null}
        metadata={metadata}
        statusLabelMap={statusLabelMap}
        severityLabelMap={severityLabelMap}
        selectedReportMetadataEntries={selectedReportMetadataEntries}
        formStatus={formStatus}
        onFormStatusChange={handleFormStatusChange}
        formClosedReason={formClosedReason}
        onFormClosedReasonChange={handleFormClosedReasonChange}
        formSeverity={formSeverity}
        onFormSeverityChange={handleFormSeverityChange}
        formDuplicate={formDuplicate}
        onClearDuplicate={() => setFormDuplicate(null)}
        requiresClosedReason={requiresClosedReason}
        requiresSeverity={requiresSeverity}
        isDuplicateRequired={isDuplicateRequired}
        formError={formError}
        onSave={handleSave}
        onReset={handleResetForm}
        isSaveDisabled={isSaveDisabled}
        saving={saving}
        editingFeedback={editingFeedback}
        feedbackDraft={feedbackDraft}
        onFeedbackDraftChange={handleFeedbackDraftChange}
        feedbackError={feedbackError}
        onStartEditingFeedback={handleStartEditingFeedback}
        onCancelEditingFeedback={handleCancelEditingFeedback}
        onSaveFeedback={handleSaveFeedback}
        feedbackSaving={feedbackSaving}
        isFeedbackSaveDisabled={isFeedbackSaveDisabled}
        duplicatePickerOpen={duplicatePickerOpen}
        onToggleDuplicatePicker={handleDuplicatePickerToggle}
        duplicateSearch={duplicateSearch}
        onDuplicateSearchChange={handleDuplicateSearchChange}
        duplicateLoading={duplicateLoading}
        duplicateResults={duplicateResults}
        onSelectDuplicate={handleSelectDuplicate}
        notes={notes}
        notesLoading={notesLoading}
        noteDraft={noteDraft}
        onNoteDraftChange={handleNoteDraftChange}
        noteSaving={noteSaving}
        onAddNote={handleAddNote}
        editingNoteId={editingNoteId}
        editingNoteValue={editingNoteValue}
        onEditingNoteValueChange={handleEditingNoteValueChange}
        editingNoteSaving={editingNoteSaving}
        editingNoteError={editingNoteError}
        onStartEditingNote={handleStartEditingNote}
        onCancelEditingNote={handleCancelEditingNote}
        onSaveEditedNote={handleSaveEditedNote}
      />
    </div>
  );
};

export default FeedbackReports;





